import { useState } from "react"
import { Box, IconButton, InputBase, Tooltip } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUpRightAndDownLeftFromCenter } from "@fortawesome/pro-solid-svg-icons"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"

import { cellFieldMissingStyle, cellFieldStyle } from "./cellFieldStyles"
import { DescriptionDialog } from "./DescriptionDialog"

const useStyles = makeStyles()((theme) => ({
    wrap: {
        position: "relative",
        "&:hover .MuiIconButton-root": {
            display: "inline-flex",
        },
    },
    input: {
        ...cellFieldStyle(theme),
        width: "100%",
        // Extra room on the right so the text never runs under the expand button.
        padding: ".25rem 1.125rem .25rem .25rem",
        color: theme.palette.tailwind.slate[600],
        fontSize: ".75rem",
        "& textarea::placeholder": {
            color: theme.palette.tailwind.slate[500],
            opacity: 1,
        },
    },
    inputError: cellFieldMissingStyle(theme),
    expandButton: {
        display: "none",
        position: "absolute",
        top: ".125rem",
        right: ".125rem",
        width: "1.25rem",
        height: "1.25rem",
        color: theme.palette.tailwind.slate[500],
        "& svg": {
            fontSize: ".5rem",
        },
    },
}))

type Props = Readonly<{
    dishName: string
    value: string
    hasError: boolean
    isOptional: boolean
    onChange: (value: string) => void
}>

export function CellDescriptionField({ dishName, value, hasError, isOptional, onChange }: Props) {
    const { classes, cx } = useStyles()
    const [isDialogOpen, setDialogOpen] = useState(false)

    return (
        <Box className={classes.wrap}>
            <LocalizedStrict
                id={
                    isOptional
                        ? "merchant-week-menu-cell-description-field-optional"
                        : "merchant-week-menu-cell-description-field"
                }
                attrs={{ placeholder: true }}
            >
                <InputBase
                    multiline
                    maxRows={3}
                    value={value}
                    placeholder="Add description…"
                    className={cx(classes.input, hasError && classes.inputError)}
                    onChange={(event) => onChange(event.target.value)}
                />
            </LocalizedStrict>
            <LocalizedStrict id="merchant-week-menu-cell-description-expand-tooltip" attrs={{ title: true }}>
                <Tooltip arrow title="Open full description">
                    <IconButton size="small" className={classes.expandButton} onClick={() => setDialogOpen(true)}>
                        <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} />
                    </IconButton>
                </Tooltip>
            </LocalizedStrict>
            {isDialogOpen && (
                <DescriptionDialog
                    open={isDialogOpen}
                    dishName={dishName}
                    initialValue={value}
                    onSave={(newValue) => {
                        onChange(newValue)
                        setDialogOpen(false)
                    }}
                    onClose={() => setDialogOpen(false)}
                />
            )}
        </Box>
    )
}
